<template>
  <div class="MyMapPlatformItemSearch">
    <label class="MyMapPlatformItemSearch__label">
      <span class="MyMapPlatformItemSearch__caption">{{ searchFieldLabel }}</span>
    </label>
    <Combobox
      :model-value="null"
      @update:model-value="onComboboxUpdate"
      as="div"
      class="relative w-full"
    >
      <div
        class="relative flex h-9 w-full cursor-default flex-wrap items-center gap-1 overflow-hidden rounded-md border border-gray-300 bg-white text-left transition-colors focus-within:border-main focus-within:outline-none focus-within:ring-1 focus-within:ring-main"
      >
        <div class="flex items-center pl-2 text-gray-500">
          <Icon icon="mdi:magnify" color="main-blue" class="shrink-0" size="18" />
        </div>
        <ComboboxInput
          class="min-w-[50px] flex-1 border-none bg-transparent py-1.5 pl-1 pr-2 text-sm leading-5 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
          :display-value="(item: unknown) => displayValue(item as PlatformMapSearchEntry | null)"
          :placeholder="placeholderText"
          autocomplete="off"
          @change="onQueryChange"
        />
      </div>

      <TransitionRoot
        leave="transition ease-in duration-100"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <ComboboxOptions
          v-if="searchQuery.trim().length >= 1"
          class="absolute z-[60] mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
        >
          <div
            v-if="filteredEntries.length === 0"
            class="cursor-default select-none px-3 py-2 text-gray-600"
          >
            {{ noResultsText }}
          </div>
          <ComboboxOption
            v-for="entry in filteredEntries"
            :key="`${entry.kind}:${entry.id}`"
            :value="entry"
            as="template"
            v-slot="{ active, selected }"
          >
            <li
              class="cursor-pointer select-none truncate px-3 py-2"
              :class="active ? 'bg-blue-50 text-main' : 'text-gray-900'"
            >
              <span class="block truncate" :class="{ 'font-medium': selected }">{{ entry.name }}</span>
            </li>
          </ComboboxOption>
        </ComboboxOptions>
      </TransitionRoot>
    </Combobox>
  </div>
</template>

<script setup lang="ts">
import Icon from '@/components/ui/Icon.vue'
import { ItemType } from '@/models/enums/app/ItemType'
import type { Project } from '@/models/interfaces/Project'
import type { Resource } from '@/models/interfaces/Resource'
import { AppLayersService } from '@/services/map/AppLayersService'
import {
  fitMapToPlatformGeoData,
  getLngLatBoundsFromGeoData,
} from '@/services/map/PlatformItemMapNavigationService'
import { useMyMapStore } from '@/stores/myMapStore'
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  TransitionRoot,
} from '@headlessui/vue'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const MAX_RESULTS = 40

const props = defineProps<{
  itemType: ItemType.PROJECT | ItemType.RESOURCE
}>()

const { t } = useI18n()
const myMapStore = useMyMapStore()

const searchQuery = ref('')

interface PlatformMapSearchEntry {
  kind: ItemType.PROJECT | ItemType.RESOURCE
  id: string
  name: string
  raw: Project | Resource
}

const placeholderText = computed(() =>
  props.itemType === ItemType.PROJECT
    ? t('myMap.leftSidebar.projectFilters.titleSearchPlaceholder')
    : t('myMap.leftSidebar.resourceFilters.titleSearchPlaceholder'),
)

const noResultsText = computed(() =>
  props.itemType === ItemType.PROJECT
    ? t('myMap.leftSidebar.projectFilters.titleSearchNoResults')
    : t('myMap.leftSidebar.resourceFilters.titleSearchNoResults'),
)

const searchFieldLabel = computed(() =>
  props.itemType === ItemType.PROJECT
    ? t('myMap.leftSidebar.projectFilters.titleSearchLabel')
    : t('myMap.leftSidebar.resourceFilters.titleSearchLabel'),
)

const sourceItems = computed((): (Project | Resource)[] => {
  if (props.itemType === ItemType.PROJECT) {
    return AppLayersService.filteredProjects.value as Project[]
  }
  return AppLayersService.filteredResources.value as Resource[]
})

const allEntries = computed((): PlatformMapSearchEntry[] => {
  const out: PlatformMapSearchEntry[] = []
  for (const item of sourceItems.value) {
    const name = String(item.name ?? '').trim()
    if (name === '') continue
    const geo = item.geoData
    if (!getLngLatBoundsFromGeoData(geo ?? null)) continue
    out.push({
      kind: props.itemType,
      id: String(item.id),
      name,
      raw: item,
    })
  }
  out.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
  return out
})

const filteredEntries = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (q.length < 1) return []
  return allEntries.value.filter((e) => e.name.toLowerCase().includes(q)).slice(0, MAX_RESULTS)
})

function displayValue(entry: PlatformMapSearchEntry | null | undefined): string {
  if (entry) return entry.name
  return searchQuery.value
}

function onQueryChange(event: Event): void {
  const target = event.target as HTMLInputElement | null
  searchQuery.value = target?.value ?? ''
}

function onComboboxUpdate(value: unknown): void {
  const entry = value as PlatformMapSearchEntry | null
  if (entry == null) return
  const mapInstance = myMapStore.map
  const geo = entry.raw.geoData
  const didFit = fitMapToPlatformGeoData(mapInstance ?? null, geo ?? null)
  if (didFit) {
    const bounds = getLngLatBoundsFromGeoData(geo ?? null)
    const center = bounds?.getCenter()
    if (center) {
      myMapStore.activeItemCoords = center
    }
    myMapStore.activeItemId = String(entry.raw.id)
  }
  searchQuery.value = ''
}
</script>

<style scoped lang="scss">
.MyMapPlatformItemSearch {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin-bottom: 0.5rem;
}

.MyMapPlatformItemSearch__label {
  margin: 0;
}

.MyMapPlatformItemSearch__caption {
  font-size: var(--panel-label-size);
  color: rgb(var(--v-theme-main-blue));
}
</style>
